import React from "react";
import {
  FileJson,
  Download,
  CheckCircle,
  Shield,
  AlertTriangle,
  Layers,
  Copy,
} from "lucide-react";
import campaignData from "../../data/campaignData.json";

const MarketoBuildPackCard = ({ streams = [], regions }) => {
  // Pulling the new executive narrative from JSON
  const narrative = campaignData.tab_narratives?.export;

  // Helper to format regions for the token display
  const regionString = regions
    ? Object.keys(regions)
        .filter((k) => regions[k])
        .join(" / ")
    : "Global";

  return (
    <div className="space-y-8 animate-in fade-in duration-500 font-sans">
      {/* 1. NARRATIVE PANEL (The Executive Advisory Layer) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="bg-slate-50 p-6 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 text-purple-700 rounded-lg">
              <FileJson size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">
                {narrative?.title || "Export Strategy"}
              </h2>
              <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                {narrative?.subtitle || "Operational Translation"}
              </p>
            </div>
          </div>
          <p className="text-slate-700 mt-2 max-w-4xl text-lg leading-relaxed">
            {narrative?.executive_overview ||
              "Ensure advisory design survives execution. This layer packages streams with clear governance, scoring, and routing logic."}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          <div className="p-6">
            <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
              <CheckCircle size={16} className="text-emerald-500" /> What You
              Get
            </h4>
            <ul className="space-y-2">
              {narrative?.what_you_get?.map((item, i) => (
                <li
                  key={i}
                  className="text-sm text-slate-600 flex items-start gap-2"
                >
                  <span className="mt-1.5 w-1 h-1 bg-slate-400 rounded-full shrink-0"></span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 bg-slate-50/50">
            <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
              <Shield size={16} className="text-amber-500" /> Why It Matters
            </h4>
            <p className="text-sm text-slate-600 leading-relaxed">
              {narrative?.why_it_matters}
            </p>
          </div>
          <div className="p-6">
            <h4 className="font-bold text-slate-900 text-sm mb-3">
              Distinction
            </h4>
            <p className="text-sm text-slate-500 italic border-l-2 border-purple-200 pl-3">
              "{narrative?.difference}"
            </p>
          </div>
        </div>
      </div>

      {/* 2. OPERATIONAL EXPORT TOOLS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* A. Token Manifest */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col">
          <div className="mb-4">
            <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
              <Layers size={18} className="text-purple-500" /> Program Tokens
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Copy these values directly into your Marketo Program "My Tokens"
              tab.
            </p>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 font-mono text-xs text-emerald-400 overflow-x-auto mb-6 flex-grow border border-slate-800 shadow-inner">
            <div className="space-y-2">
              <div className="flex gap-2">
                <span className="text-purple-400">my.Region</span> ={" "}
                <span className="text-white">"{regionString}"</span>
              </div>
              <div className="flex gap-2">
                <span className="text-purple-400">my.Vertical</span> ={" "}
                <span className="text-white">"Technology"</span>
              </div>
              <div className="flex gap-2">
                <span className="text-purple-400">my.Persona</span> ={" "}
                <span className="text-white">"L&D Leader"</span>
              </div>
              <div className="flex gap-2">
                <span className="text-purple-400">my.Score_Threshold</span> ={" "}
                <span className="text-white">"60"</span>
              </div>
              <div className="flex gap-2">
                <span className="text-purple-400">my.Wait_Step</span> ={" "}
                <span className="text-white">"10 Days"</span>
              </div>
            </div>
          </div>

          <button className="w-full flex items-center justify-center gap-2 bg-purple-50 hover:bg-purple-100 text-purple-700 font-bold py-2.5 rounded-lg transition-colors border border-purple-200 group">
            <Copy
              size={16}
              className="group-hover:scale-110 transition-transform"
            />{" "}
            Copy Token Map
          </button>
        </div>

        {/* B. QA Checklist */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col">
          <div className="mb-4">
            <h3 className="font-bold text-slate-900 text-lg flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" /> Pre-Launch
              QA
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Critical governance checks required before activation.
            </p>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 mb-6 flex-grow">
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-slate-300"
                  />
                </div>
                <div>
                  <span className="text-sm font-bold text-slate-700 block">
                    Verify Exclusion Logic
                  </span>
                  <span className="text-xs text-slate-500">
                    Ensure 'Opportunity Open' smart list is referenced in flow
                    step 1.
                  </span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-slate-300"
                  />
                </div>
                <div>
                  <span className="text-sm font-bold text-slate-700 block">
                    Confirm Sales Handoff
                  </span>
                  <span className="text-xs text-slate-500">
                    Test that score &gt; 60 triggers 'MQL' lifecycle status
                    change.
                  </span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="mt-0.5">
                  <input
                    type="checkbox"
                    className="w-4 h-4 rounded text-purple-600 focus:ring-purple-500 border-slate-300"
                  />
                </div>
                <div>
                  <span className="text-sm font-bold text-slate-700 block">
                    Validate LEAP Scoring
                  </span>
                  <span className="text-xs text-slate-500">
                    Check that 'Choice Logic' interaction awards correct +12
                    points.
                  </span>
                </div>
              </li>
            </ul>
          </div>

          <button className="w-full flex items-center justify-center gap-2 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold py-2.5 rounded-lg transition-colors border border-slate-200">
            <Download size={16} /> Export QA Report (PDF)
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketoBuildPackCard;
